import React from 'react';
import Text from '../../atoms/Text';

export type TCaseVariant =
	| 'success'
	| 'error'
	| 'pending'
	| 'inactive'
	| 'loading'
	| 'filter'
	| 'alert';
interface Props {
	label: string;
	onClick?: () => void;
	type: 'filled' | 'contrast' | 'text';
	caseVariant: TCaseVariant;
	iconLeft?: React.ReactNode;
	iconRight?: React.ReactNode;
	quantity?: number;
	sx?: string;
	isSelected?: boolean;
	disabled?: boolean;
}

const Tag: React.FC<Props> = ({
	label,
	onClick,
	type,
	caseVariant,
	iconLeft,
	iconRight,
	quantity,
	sx,
	isSelected,
	disabled = false,
}) => {
	const bgColor: Record<
		string,
		{
			success: string;
			error: string;
			pending: string;
			inactive: string;
			loading: string;
			filter: string;
			alert: string;
		}
	> = {
		filled: {
			success: 'bg-h-success-90',
			error: 'bg-h-error-90',
			pending: 'bg-h-warning-90',
			inactive: 'bg-h-neutral-90',
			loading: 'bg-h-information-90',
			filter: 'bg-h-neutral-94 shadow',
			alert: 'bg-h-warning-70',
		},
		contrast: {
			success: 'bg-h-success-50',
			error: 'bg-h-error-50',
			pending: 'bg-h-warning-50',
			inactive: 'bg-h-neutral-60',
			loading: 'bg-h-information-50',
			filter: 'bg-h-neutral-94',
			alert: 'bg-h-warning-70',
		},
		text: {
			success: 'bg-transparent',
			error: 'bg-transparent',
			pending: 'bg-transparent',
			inactive: 'bg-transparent',
			loading: 'bg-transparent',
			filter: 'bg-transparent',
			alert: 'bg-transparent',
		},
	};

	const textColor: Record<
		string,
		{
			success: string;
			error: string;
			pending: string;
			inactive: string;
			loading: string;
			filter: string;
			alert: string;
		}
	> = {
		filled: {
			success: 'text-h-success-50',
			error: 'text-h-error-50',
			pending: 'text-h-warning-30',
			inactive: 'text-h-neutral-20',
			loading: 'text-h-information-50',
			filter: 'text-h-neutral-20',
			alert: 'text-red-800',
		},
		contrast: {
			success: 'text-white',
			error: 'text-white',
			pending: 'text-white',
			inactive: 'text-white',
			loading: 'text-white',
			filter: 'text-neutral-60',
			alert: 'text-h-warning-50',
		},
		text: {
			success: 'text-h-success-50',
			error: 'text-h-error-50',
			pending: 'text-h-warning-30',
			inactive: 'text-h-neutral-20',
			loading: 'text-h-information-50',
			filter: 'text-h-filter-50',
			alert: 'text-red-800',
		},
	};

	const iconColor: Record<
		string,
		{
			success: string;
			error: string;
			pending: string;
			inactive: string;
			loading: string;
			filter: string;
			alert: string;
		}
	> = {
		filled: {
			success: 'text-h-success-50',
			error: 'text-h-error-50',
			pending: 'text-h-warning-30',
			inactive: 'text-h-neutral-20',
			loading: '!text-h-information-50',
			filter: 'text-h-neutral-20',
			alert: 'text-h-warning-30',
		},
		contrast: {
			success: 'text-white',
			error: 'text-white',
			pending: 'text-white',
			inactive: 'text-white',
			loading: 'text-white',
			filter: 'text-neutral-60',
			alert: 'text-white',
		},
		text: {
			success: 'text-h-success-50',
			error: 'text-h-error-50',
			pending: 'text-h-warning-50',
			inactive: 'text-h-neutral-20',
			loading: '!text-h-information-50',
			filter: 'text-h-filter-50',
			alert: 'text-h-warning-50',
		},
	};

	const getStyles = () => {
		if (isSelected) {
			return {
				bg: 'bg-[#dce7f0]',
				text: 'text-[#2e84c5]', 
				icon: 'text-[#2e84c5]',
			};
		}
		return {
			bg: bgColor[type][caseVariant],
			text: textColor[type][caseVariant],
			icon: iconColor[type][caseVariant],
		};
	};

	const styles = getStyles();

	return (
		<div
			onClick={disabled ? undefined : onClick}
			className={`flex items-center ${
				type !== 'text' && 'gap-1'
				} ${onClick && !disabled ? 'cursor-pointer' : ''} ${
					disabled ? 'opacity-50 cursor-not-allowed' : ''
				}`}
		>
			<Text
				size='mini-1'
				weight='regular'
				sx={`flex items-center px-3 py-1 rounded-full min-h-6
                ${styles.text}
                ${styles.bg} ${sx}`}
			>
				<span
					className={`mr-[-3px] ${styles.icon}`}
				>
					{iconLeft}
				</span>
				{label && (
					<span className={`leading-3 ${styles.text}`}>
						{label}
					</span>
				)}

				<span
					className={`ml-[-3px] ${styles.icon}`}
				>
					{iconRight}
				</span>
			</Text>
			{quantity && (
				<Text
					size='mini-1'
					weight='regular'
					sx={`${styles.text}`}
				>
					<span className={`${styles.text}`}>{quantity}</span>
				</Text>
			)}
		</div>
	);
};

export default Tag;
